import { easeThemeReveal } from './themeRevealEasing';
type ViewTransition = {
	finished: Promise<void>;
	ready: Promise<void>;
	skipTransition: () => void;
};
type ViewTransitionDocument = Document & {
	startViewTransition?: (update: () => void) => ViewTransition;
};
type ThemeRevealWindow = Window & {
	__themeRevealTest?: { holdCleanup?: boolean; holdMid?: boolean };
	__resumeThemeRevealMid?: () => void;
	__resumeThemeRevealCleanup?: () => void;
};

type ThemeRevealRun = {
	id: number;
	cancelled: boolean;
	applyTheme: () => void;
	themeApplied: boolean;
	radiusComplete: boolean;
	transitionFinished: boolean;
	ball: HTMLElement | null;
	geometry: SVGSVGElement | null;
	style: HTMLStyleElement | null;
	circle: SVGCircleElement | null;
	clipId: string;
	transition: ViewTransition | null;
	timeoutId: number | null;
	rafId: number | null;
	resizeHandler: (() => void) | null;
};

const BALL_SIZE = 80;
const BOUNCE_DURATION_MS = 1500;
const REVEAL_DURATION_MS = 850;
const HOLD_DURATION_MS = 900;
const TEST_HOLD_DURATION_MS = 60000;

let activeThemeRun: ThemeRevealRun | null = null;
let nextThemeRunId = 0;

function revealWindow() {
	return window as ThemeRevealWindow;
}

function isCurrentThemeRun(run: ThemeRevealRun) {
	return activeThemeRun === run && !run.cancelled;
}

function emitThemeRevealPhase(run: ThemeRevealRun, phase: string, extra: Record<string, number> = {}) {
	window.dispatchEvent(new CustomEvent('theme-reveal:phase', { detail: { phase, runId: run.id, ...extra } }));
}

function removeRunResources(run: ThemeRevealRun) {
	if (run.timeoutId !== null) window.clearTimeout(run.timeoutId);
	if (run.rafId !== null) window.cancelAnimationFrame(run.rafId);
	if (run.resizeHandler) window.removeEventListener('resize', run.resizeHandler);
	run.ball?.remove();
	run.geometry?.remove();
	run.style?.remove();
	run.ball = null;
	run.geometry = null;
	run.style = null;
}

function clearTestHooks() {
	const target = revealWindow();
	delete target.__resumeThemeRevealMid;
	delete target.__resumeThemeRevealCleanup;
}

function finalizeThemeRun(run: ThemeRevealRun) {
	if (!isCurrentThemeRun(run)) return;
	emitThemeRevealPhase(run, 'cleanup-requested');
	activeThemeRun = null;
	removeRunResources(run);
	document.documentElement.classList.remove('theme-reveal-active', 'theme-transitioning');
	document.documentElement.style.removeProperty('--theme-reveal-hold-duration');
	clearTestHooks();
	window.requestAnimationFrame(() => {
		if (nextThemeRunId !== run.id) return;
		emitThemeRevealPhase(run, 'cleanup-painted');
	});
}

export function cancelThemeReveal() {
	const run = activeThemeRun;
	if (!run) return;
	run.cancelled = true;
	activeThemeRun = null;
	removeRunResources(run);
	try {
		run.transition?.skipTransition();
	} catch {
		// A skipped/finished transition has no remaining work to cancel.
	}
	document.documentElement.classList.remove('theme-reveal-active', 'theme-transitioning');
	document.documentElement.style.removeProperty('--theme-reveal-hold-duration');
	clearTestHooks();
}

function applyRunTheme(run: ThemeRevealRun) {
	if (!isCurrentThemeRun(run) || run.themeApplied) return;
	try {
		run.applyTheme();
	} catch {
		// The callback commits the document theme before optional persistence/UI
		// work. A denied storage write must not strand View Transition resources.
	} finally {
		run.themeApplied = true;
	}
}

function mountRevealGeometry(run: ThemeRevealRun, centerX: number, centerY: number) {
	const svgNamespace = 'http://www.w3.org/2000/svg';
	const clipId = `theme-reveal-clip-${run.id}`;
	const svg = document.createElementNS(svgNamespace, 'svg');
	svg.classList.add('theme-reveal-geometry');
	svg.setAttribute('data-theme-reveal-geometry', String(run.id));
	svg.setAttribute('aria-hidden', 'true');

	const defs = document.createElementNS(svgNamespace, 'defs');
	const clip = document.createElementNS(svgNamespace, 'clipPath');
	clip.setAttribute('id', clipId);
	clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
	const circle = document.createElementNS(svgNamespace, 'circle');
	circle.setAttribute('data-theme-reveal-circle', String(run.id));
	circle.setAttribute('cx', String(centerX));
	circle.setAttribute('cy', String(centerY));
	circle.setAttribute('r', String(BALL_SIZE / 2));
	clip.appendChild(circle);
	defs.appendChild(clip);
	svg.appendChild(defs);
	document.body.appendChild(svg);
	run.geometry = svg;

	const style = document.createElement('style');
	style.setAttribute('data-theme-reveal-style', String(run.id));
	style.textContent =
		`html.theme-reveal-active::view-transition-new(root){clip-path:url("#${clipId}") !important;` +
		`-webkit-clip-path:url("#${clipId}") !important;}`;
	document.head.appendChild(style);

	run.style = style;
	run.circle = circle;
	run.clipId = clipId;
}

function fallBackToFinalTheme(run: ThemeRevealRun) {
	if (!isCurrentThemeRun(run)) return;
	applyRunTheme(run);
	try {
		run.transition?.skipTransition();
	} catch {
		// The target theme is already committed; cleanup is still safe.
	}
	finalizeThemeRun(run);
}

function animateRevealRadius(run: ThemeRevealRun, centerX: number, centerY: number, endRadius: number) {
	let startTime: number | null = null;
	let midEmitted = false;
	const tick = (now: number) => {
		if (!isCurrentThemeRun(run) || !run.circle) return;
		startTime ??= now;
		const progress = Math.min(1, (now - startTime) / REVEAL_DURATION_MS);
		const radius = BALL_SIZE / 2 + (endRadius - BALL_SIZE / 2) * easeThemeReveal(progress);
		run.circle.setAttribute('r', String(radius));

		if (!midEmitted && radius >= endRadius * 0.55) {
			midEmitted = true;
			emitThemeRevealPhase(run, 'mid', { centerX, centerY, ballCenterX: centerX, ballCenterY: centerY, radius, endRadius });
			const target = revealWindow();
			if (target.__themeRevealTest?.holdMid) {
				target.__resumeThemeRevealMid = () => {
					if (!isCurrentThemeRun(run)) return;
					delete target.__resumeThemeRevealMid;
					run.rafId = window.requestAnimationFrame(tick);
				};
				return;
			}
		}

		if (progress < 1) {
			run.rafId = window.requestAnimationFrame(tick);
			return;
		}

		run.radiusComplete = true;
		emitThemeRevealPhase(run, 'radius-complete', { centerX, centerY, radius: endRadius, endRadius });
		const target = revealWindow();
		if (target.__themeRevealTest?.holdCleanup) {
			target.__resumeThemeRevealCleanup = () => {
				if (!isCurrentThemeRun(run)) return;
				try {
					run.transition?.skipTransition();
				} catch {
					finalizeThemeRun(run);
				}
			};
		} else if (run.transitionFinished) {
			finalizeThemeRun(run);
		}
	};
	run.rafId = window.requestAnimationFrame(tick);
}

function revealNewTheme(run: ThemeRevealRun) {
	if (!isCurrentThemeRun(run) || !run.ball) return;
	const html = document.documentElement;
	const ballRect = run.ball.getBoundingClientRect();
	const centerX = ballRect.left + ballRect.width / 2;
	const centerY = ballRect.top + ballRect.height / 2;
	const endRadius = Math.ceil(Math.hypot(Math.max(centerX, window.innerWidth - centerX), Math.max(centerY, window.innerHeight - centerY)));
	const startViewTransition = (document as ViewTransitionDocument).startViewTransition;

	if (!startViewTransition) {
		applyRunTheme(run);
		finalizeThemeRun(run);
		return;
	}

	try {
		mountRevealGeometry(run, centerX, centerY);
		const target = revealWindow();
		html.style.setProperty(
			'--theme-reveal-hold-duration',
			target.__themeRevealTest?.holdCleanup ? `${TEST_HOLD_DURATION_MS}ms` : `${HOLD_DURATION_MS}ms`,
		);
		html.classList.add('theme-reveal-active', 'theme-transitioning');

		run.resizeHandler = () => {
			if (!isCurrentThemeRun(run)) return;
			applyRunTheme(run);
			cancelThemeReveal();
		};
		window.addEventListener('resize', run.resizeHandler, { once: true });

		run.transition = startViewTransition.call(document, () => {
			if (!isCurrentThemeRun(run)) return;
			applyRunTheme(run);
			run.ball?.remove();
			run.ball = null;
		});
	} catch {
		fallBackToFinalTheme(run);
		return;
	}

	void run.transition.ready
		.then(() => {
			if (!isCurrentThemeRun(run)) return;
			const newSnapshotStyle = window.getComputedStyle(html, '::view-transition-new(root)');
			const appliedClip =
				newSnapshotStyle.clipPath || (newSnapshotStyle as CSSStyleDeclaration & { webkitClipPath?: string }).webkitClipPath || '';
			if (appliedClip === 'none' || !appliedClip.includes(run.clipId)) {
				fallBackToFinalTheme(run);
				return;
			}
			emitThemeRevealPhase(run, 'ready', { centerX, centerY, ballCenterX: centerX, ballCenterY: centerY, endRadius });
			animateRevealRadius(run, centerX, centerY, endRadius);
		})
		.catch(() => fallBackToFinalTheme(run));

	void run.transition.finished.then(
		() => {
			if (!isCurrentThemeRun(run)) return;
			run.transitionFinished = true;
			if (run.radiusComplete) finalizeThemeRun(run);
		},
		() => {
			if (isCurrentThemeRun(run)) finalizeThemeRun(run);
		},
	);
}

/**
 * Drops and bounces the theme ball, then reveals the complete rendered new
 * theme snapshot through SVG viewport-space geometry.
 */
export function runThemeReveal({ ballColor, applyTheme }: { ballColor: string; applyTheme: () => void }) {
	cancelThemeReveal();
	const run: ThemeRevealRun = {
		id: ++nextThemeRunId,
		cancelled: false,
		applyTheme,
		themeApplied: false,
		radiusComplete: false,
		transitionFinished: false,
		ball: null,
		geometry: null,
		style: null,
		circle: null,
		clipId: '',
		transition: null,
		timeoutId: null,
		rafId: null,
		resizeHandler: null,
	};
	activeThemeRun = run;

	const ball = document.createElement('div');
	ball.className = 'theme-ball';
	ball.style.background = ballColor;
	document.body.appendChild(ball);
	run.ball = ball;

	void ball.offsetWidth;
	ball.classList.add('theme-ball-bounce');

	let completed = false;
	const completeBounce = () => {
		if (completed || !isCurrentThemeRun(run)) return;
		completed = true;
		revealNewTheme(run);
	};
	ball.addEventListener('animationend', completeBounce, { once: true });
	run.timeoutId = window.setTimeout(completeBounce, BOUNCE_DURATION_MS + 200);
}
